import assert from '@/sui/tools/assert'

import units from '@/sui/data/units'
import micro from '@/sui/data/microOrgs'
import fields from '@/sui/data/sheets/edit/fields'

import moment from 'moment'

function clientField (operation) {
  if (operation.cfield != null) {
    return operation.cfield
  }

  return operation.field
}

const none = {
  clientToServer: function (client, server, errors, op) {
  },

  serverToClient: function (client, server, errors, op) {
  }
}

const numeric = {
  clientToServer: function (client, server, errors, op) {
    const str = client[clientField(op)]
    if (str == null) {
      return
    }

    const value = parseInt(str, 10)
    if (isNaN(value)) {
      errors.push(`Invalid value for numeric field ${op.field}: ${str}.`)
      return
    }

    server[op.field] = value
  },

  serverToClient: function (client, server, errors, op) {
    const value = server[op.field]
    if (value == null) {
      return
    }

    client[clientField(op)] = String(value)
  }
}

const date = {
  clientToServer: function (client, server, error, op) {
    const str = client[clientField(op)]
    if (str == null) {
      return
    }

    const values = str.split('/')
    const day = parseInt(values[0], 10)
    const month = parseInt(values[1], 10)
    const year = parseInt(values[2], 10)
    server[op.field] = new Date(year, month - 1, day, 10)
  },

  serverToClient: function (client, server, error, op) {
    const date = server[op.field]
    if (date == null) {
      return
    }

    const value = new Date(date)
    if (value.getFullYear() > 1) {
      const str = moment(date).format('DD/MM/YYYY')
      client[clientField(op)] = str
    }
  }
}

function codeAndValues (name, values) {
  return {
    values: values,

    clientToServer: function (client, server, errors, op) {
      const str = client[clientField(op)]
      if (str == null) {
        return
      }
      const result = this.values.find(e => e.code === str)
      if (result == null) {
        errors.push(`Invalid ${name}: ${str}.`)
      }

      server[op.field] = result.index
    },

    serverToClient: function (client, server, errors, op) {
      const value = server[op.field]
      if (value == null) {
        return
      }
      const result = this.values.find(e => e.index === value)
      if (result == null) {
        errors.push(`Invalid ${name} value: ${value}.`)
      }

      client[clientField(op)] = result.code
    }
  }
}

function buildAntibio (v) {
  const resistances = micro.resistances[v]
  return {
    clientToServer: function (client, server, errors, op) {
      const result = []
      const list = resistances
      for (let i in list) {
        const l = list[i]
        const it = client[l.field]
        if (it != null) {
          const resistance = l.values.find(e => e.code === it)
          if (resistance == null) {
            errors.push(`Invalid resistance: ${it}.`)
            return
          }
          result.push({
            code: l.index,
            resistance: resistance.index
          })
        }
      }
      server[op.field] = result
    },

    serverToClient: function (client, server, errors, op) {
      const list = server[op.field]
      for (let l in list) {
        const it = list[l]
        const resistance = resistances.find(e => e.index === it.code)
        if (resistance == null) {
          errors.push(`Unknown code index: ${it.code}.`)
          return
        }

        const value = resistance.values.find(e => e.index === it.resistance)
        if (value == null) {
          errors.push(`Unknown resistance index: ${it.resistance}`)
          return
        }
        client[resistance.field] = value.code
      }
    }
  }
}

function microOrg (viruses) {
  const list = viruses ? micro.completeList.v2 : micro.list.v2

  return {
    clientToServer: function (client, server, errors, op) {
      const str = client[clientField(op)]
      const result = list.find(e => e.code === str)
      if (result == null) {
        errors.push(`Invalid micro organism code: ${str}.`)
      }

      server[op.field] = result.index
    },

    serverToClient: function (client, server, errors, op) {
      const value = server[op.field]
      const result = list.find(e => e.index === value)
      if (result == null) {
        errors.push(`Invalid origin value: ${value}.`)
      }

      client[clientField(op)] = result.code
    }
  }
}

const mainSeverity = {
  clientToServer: function (client, server, errors, op) {
    let unit = client[fields.unitType]
    if (unit == null) {
      unit = client[`sep${fields.unitType}`]

      if (unit == null) {
        return
      }
    }

    let type = null
    const mix = unit.match(/^MIX/) != null
    if (!mix) {
      if (unit.match(/^NEO/) != null) {
        type = 'neo'
      } else if (unit.match(/^PED/) != null) {
        type = 'ped'
      } else {
        type = 'ad'
      }
    } else if (client.type === 'neoInfADI') {
      type = 'neo'
    } else if (client.type === 'reaInfADI') {
      type = 'ped'
    } else if (client.type === 'expADI' && client.expManagment === 3) {
      type = 'neo'
    } else if (client.mixManagment === 1) {
      type = 'ped'
    } else if (client.mixManagment === 2) {
      type = 'neo'
    }

    if (type == null) {
      return
    }

    let score = ''
    switch (type) {
      case 'neo':
        score = 'cribII'
        break
      case 'ped':
        score = 'pimII'
        break
      case 'ad':
        score = 'igsII'
    }

    const str = client[score]
    if (str == null || str === '') {
      return
    }

    const value = parseInt(str, 10)
    if (isNaN(value)) {
      errors.push(`Invalid value for numeric field ${score}: ${str}.`)
      return
    }

    server[op.field] = { code: score, value: value }
  },

  serverToClient: function (client, server, errors, op) {
    const value = server[op.field]
    if (value == null) {
      return
    }

    client[value.code] = String(value.value)
  }
}

const otherSeverity = {
  clientToServer: function (client, server, errors, op) {
    const result = {
      code: client['severityType']
    }

    const str = client['severityScore']
    if (str != null && str !== '') {
      const value = parseInt(str, 10)
      if (isNaN(value)) {
        errors.push(`Invalid value for numeric field severityScore: ${str}.`)
        return
      }
      result.value = value
    }

    server[op.field] = result
  },

  serverToClient: function (client, server, errors, op) {
    const value = server[op.field]
    if (value == null) {
      return
    }

    if (value.code != null) {
      client['severityType'] = String(value.code)
    }
    if (value.value != null) {
      client['severityScore'] = String(value.value)
    }
  }
}

const pickList = {
  clientToServer: function (client, server, errors, op) {
    const pickListData = client[op.cfield]
    if (pickListData == null) {
      return
    }

    const keys = Object.keys(pickListData)
    const result = []
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const value = pickListData[key]

      assert.isString(key)
      assert.isBoolean(value)
      result.push({ key: key, value: value })
    }

    server[op.field] = result
  },

  serverToClient: function (client, server, errors, op) {
    const pickListData = server[op.field]
    if (pickListData == null) {
      return
    }

    const result = {}
    for (let i = 0; i < pickListData.length; i++) {
      const { key, value } = pickListData[i]
      assert.isString(key)
      assert.isBoolean(value)

      result[key] = value
    }

    client[op.cfield] = result
  }
}

export function buildConvertFromFieldList (fields) {
  assert.notNull(fields)
  assert.isArray(fields)

  const result = []
  const stack = [{ target: result, source: fields }]
  while (stack.length > 0) {
    const { target, source } = stack.pop()
    for (let i = 0; i !== source.length; ++i) {
      const item = source[i]

      const convert = {
        field: item.convert.server ? item.convert.server : item.field,
        cfield: item.convert.server ? item.field : undefined,
        special: item.convert.special
      }

      if (item.type === 'numeric' && convert.special == null) {
        convert.special = 'numeric'
      } else if (item.type === 'date' && convert.special == null) {
        convert.special = 'date'
      } else if (item.type === 'pickList' && convert.special == null) {
        convert.special = 'pickList'
      } else if (item.type === 'array' && convert.array == null) {
        convert.array = []
        stack.push({ target: convert.array, source: item.config.fields })
      }

      target.push(convert)
    }
  }
  return result
}

export default {
  clientField: clientField,
  none,
  numeric: numeric,
  date: date,
  pickList: pickList,
  unitType: codeAndValues('unitType', units['2023'].list),
  unitType_v2: codeAndValues('unitType', units['2024'].list),
  antibio: buildAntibio('v1'),
  antibio_v2: buildAntibio('v2'),
  mainSeverity: mainSeverity,
  otherSeverity: otherSeverity,
  microOrgs: microOrg(false),
  microOrgsAndViruses: microOrg(true),
  buildConvertFromFieldList

}
